import PagedCollection from 'core/model/PagedCollection';
import $notifications from 'app/stores/notifications/$notifications';
import $alerting from 'app/stores/alerting/$alerting';
import $journeys from 'app/stores/ai/$journeys';
import InvestigationConfigModel from './InvestigationConfigModel';

class InvestigationConfigCollection extends PagedCollection {
  get model() {
    return InvestigationConfigModel;
  }

  get url() {
    return '/api/ui/investigation-config';
  }

  /**
   * Fetch investigation configs with pagination
   * @param {Object} options - Fetch options
   * @param {number} [options.offset=0] - Starting offset for pagination
   * @param {number} [options.limit=100] - Number of items to fetch
   * @param {boolean} [options.include_investigations=true] - Include investigations for each config
   * @param {boolean} [options.force=false] - Force refetch even if recently fetched
   * @param {boolean} [options.skipPrefetch=false] - Skip prefetching notifications and policies
   * @returns {Promise}
   */
  fetch(options = {}) {
    const {
      offset = 0,
      limit = 100,
      include_investigations = true,
      force = false,
      skipPrefetch = false,
      start_time,
      end_time,
      ...fetchOptions
    } = options;

    // Set requestStatus to 'fetching' immediately so UI shows loading state
    this.requestStatus = 'fetching';

    // Build prefetch promises
    const prefetchPromises = skipPrefetch
      ? []
      : [
          $notifications.collection.fetch({ force }),
          $alerting.policyCollection.fetch({ force }),
          // Fetch chat sessions so we have session details for investigations
          ...(include_investigations && $journeys.isEnabled ? [$journeys.chatSessionCollection.fetch({ force })] : [])
        ];

    const ready = prefetchPromises.length > 0 ? Promise.all(prefetchPromises) : Promise.resolve();

    return ready
      .then(() =>
        super.fetch({
          query: { offset, limit, include_investigations, start_time, end_time },
          force,
          ...fetchOptions
        })
      )
      .catch((error) => {
        // Ensure requestStatus is cleared on error
        this.requestStatus = null;
        throw error;
      });
  }

  /**
   * Override processData to restructure API response for PagedCollection
   * API returns: { configs: [...], total_count: number }
   * PagedCollection expects: { data: [...], pagination: { total_count: number } }
   */
  processData(response) {
    // If response has the expected structure from our API, transform it
    if (response && !Array.isArray(response) && response.configs) {
      const transformedResponse = {
        ...response,
        pagination: {
          total_count: response.total_count
        }
      };
      return super.processData(transformedResponse);
    }

    return super.processData(response);
  }

  /**
   * Deserialize: Extract configs array from response
   */
  deserialize(response) {
    // Handle array response (shouldn't happen but just in case)
    if (Array.isArray(response)) {
      return response;
    }

    // Return the configs array for Collection to process
    return response.configs || [];
  }

  /**
   * Check if a rule has investigation config enabled
   * @param {string} ruleId
   * @returns {boolean}
   */
  isEnabled(ruleId) {
    const config = this.get(ruleId);
    return config?.enabled ?? false;
  }
}

export default InvestigationConfigCollection;
